import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { DatePicker } from '@deepguide-ai/ui/date-picker'
import { Textarea } from '@deepguide-ai/ui/textarea'

export type WizardOption = {
  value: string
  label: string
  description?: string
  group?: string
  typeILabel?: string
  disabled?: boolean
  /** Mark this option as the suggested default — renders the "Recommended" badge. */
  recommended?: boolean
}

/** Suggested pill — bg tint rgb(65,221,92); label rgb(24,172,113) */
const suggestedTypeILabelClass =
  'bg-[rgba(65,221,92,0.14)] text-[rgb(24,172,113)] dark:bg-[rgba(65,221,92,0.2)] dark:text-[rgb(24,172,113)]'

// ---------- TextInput ----------

export function TextInput({
  value,
  onChange,
  placeholder,
  onEnter,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  onEnter?: () => void
}) {
  return (
    <input
      type="text"
      className="w-full border-b-2 border-border bg-card px-2 py-3 text-sm outline-none transition-colors focus:border-primary"
      placeholder={placeholder ?? 'Type your answer…'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
    />
  )
}

// ---------- NumberInput ----------

export function NumberInput({
  value,
  onChange,
  placeholder,
  onEnter,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  onEnter?: () => void
}) {
  return (
    <input
      type="number"
      min={1}
      className="w-full border-b-2 border-border bg-card px-2 py-3 text-sm outline-none transition-colors focus:border-primary"
      placeholder={placeholder ?? 'Enter a number…'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
    />
  )
}

// ---------- DateInput ----------

export function DateInput({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const dateValue = value ? new Date(value) : undefined
  return (
    <DatePicker
      value={dateValue}
      onChange={(d) => onChange(d ? d.toISOString().slice(0, 10) : '')}
      placeholder="Pick a date"
      fromDate={new Date()}
    />
  )
}

// ---------- TextareaInput ----------

export function TextareaInput({
  value,
  onChange,
  placeholder,
  onSubmit,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  onSubmit?: () => void
}) {
  return (
    <Textarea
      rows={5}
      className="resize-none p-4"
      placeholder={placeholder ?? 'Type your answer…'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault()
          onSubmit?.()
        }
      }}
    />
  )
}

/** Animated check — no hover/tap scale (row is a label, not a nested button). */
function WizardOptionCheckbox({
  checked,
  disabled,
  shape = 'square',
  onCheckedChange,
}: {
  checked: boolean
  disabled?: boolean
  /** `round` for single-select rows; `square` for multi-select. */
  shape?: 'square' | 'round'
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      preventUncheck={shape === 'round'}
      whileHover={false}
      whileTap={false}
      indicatorViewBox={shape === 'round' ? '0 0 16 16' : undefined}
      indicatorPath={
        shape === 'round' ? 'M4 8 L7 11 L12 5' : undefined
      }
      indicatorStrokeWidth={shape === 'round' ? 2.25 : undefined}
      indicatorClassName={shape === 'round' ? 'size-3.5 block' : undefined}
      className={cn(
        'shrink-0',
        shape === 'round'
          ? 'size-4 rounded-full border-border-divider'
          // `rounded-sm` is 8px in this system, so a size-4 (16px) box came out
          // as a perfect circle — indistinguishable from the round radio shape.
          // The primitive's own size-5 + rounded-sm is the rounded square every
          // other checkbox in the app uses; don't override it.
          : ''
      )}
      onCheckedChange={(state) => {
        if (disabled || state === 'indeterminate') return
        onCheckedChange(state === true)
      }}
    />
  )
}

// ---------- RadioInput ----------

export function RadioInput({
  options,
  value,
  onChange,
}: {
  options: WizardOption[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="overflow-hidden rounded-sm border border-border-divider bg-card">
      {options.map((opt, i) => {
        const isSelected = value === opt.value
        const disabled = opt.disabled === true
        return (
          <div key={opt.value}>
            {i > 0 && <div className="h-px bg-border" />}
            <label
              aria-disabled={disabled || undefined}
              className={cn(
                'flex w-full cursor-pointer items-center gap-2.5 px-4 py-3 text-left transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
                isSelected ? 'bg-primary-xsubtle' : 'bg-card hover:bg-surface-subtle',
                disabled && 'pointer-events-none cursor-not-allowed opacity-60'
              )}
            >
              <WizardOptionCheckbox
                shape="round"
                checked={isSelected}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  if (!disabled && checked) onChange(opt.value)
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="font-heading text-sm font-medium text-text-secondary">
                  {opt.label}
                </div>
                {opt.description && (
                  <div className="mt-0.5 font-body text-xs leading-[1.45] text-text-subtle">
                    {opt.description}
                  </div>
                )}
              </div>
              {opt.recommended === true && (
                <span className='shrink-0 rounded-sm bg-cobalt-tonal px-2 py-0.5 font-heading text-[10px] font-medium tracking-[0.3px] text-cobalt-strong'>
                  Recommended
                </span>
              )}
            </label>
          </div>
        )
      })}
    </div>
  )
}

// ---------- CheckboxInput ----------

export function CheckboxInput({
  options,
  value,
  onChange,
}: {
  options: WizardOption[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const toggle = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  }
  return (
    <div className="space-y-2">
      {options.map((opt) => {
        const checked = value.includes(opt.value)
        const disabled = opt.disabled === true
        return (
          <label
            key={opt.value}
            aria-disabled={disabled || undefined}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2.5 rounded-sm border px-5 py-3 text-left transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
              checked
                ? 'border-ring/50 bg-primary-xsubtle'
                : 'border-border bg-card hover:border-primary/50 hover:bg-muted/40 dark:hover:border-border-divider-hover',
              disabled && 'pointer-events-none cursor-not-allowed opacity-60'
            )}
          >
            <WizardOptionCheckbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={() => {
                if (!disabled) toggle(opt.value)
              }}
            />
            <div className="min-w-0 flex-1">
              <div className="font-heading text-sm font-medium text-text-secondary">
                {opt.label}
              </div>
              {opt.description && (
                <div className="mt-0.5 font-body text-xs leading-[1.45] text-text-subtle">
                  {opt.description}
                </div>
              )}
            </div>
            {opt.recommended === true && (
              <span className='shrink-0 rounded-sm bg-cobalt-tonal px-2 py-0.5 font-heading text-[10px] font-medium tracking-[0.3px] text-cobalt-strong'>
                Recommended
              </span>
            )}
            {opt.typeILabel && (
              <span
                className={cn(
                  'shrink-0 rounded-sm px-2 py-0.5 text-[10px] font-medium',
                  opt.typeILabel === 'Suggested'
                    ? suggestedTypeILabelClass
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {opt.typeILabel}
              </span>
            )}
          </label>
        )
      })}
    </div>
  )
}

// ---------- CheckboxWithOtherInput ----------

export function CheckboxWithOtherInput({
  options,
  value,
  onChange,
  /**
   * Opt-in "Select all". Off by default: on a list like security tools or
   * cloud providers, "all of them" is not a claim anyone should make in one
   * click. Screens where it IS the common answer (scope exclusions) turn it
   * on via `meta.selectAll`.
   */
  allowSelectAll = false,
}: {
  options: WizardOption[]
  value: string[]
  onChange: (v: string[]) => void
  allowSelectAll?: boolean
}) {
  const UNGROUPED_KEY = '__ungrouped__'
  const [otherTextByGroup, setOtherTextByGroup] = useState<Record<string, string>>({})
  const [customEntryGroupByValue, setCustomEntryGroupByValue] = useState<Record<string, string>>({})
  const knownValues = new Set(options.map((o) => o.value))

  const prevOptionsRef = useRef(options)
  useEffect(() => {
    if (prevOptionsRef.current !== options) {
      setOtherTextByGroup({})
      setCustomEntryGroupByValue({})
      prevOptionsRef.current = options
    }
  }, [options])

  const customEntries = value.filter((v) => !knownValues.has(v))

  const toggleOption = (v: string) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v])
  }

  const addCustomEntry = (groupKey: string, rawValue?: string) => {
    const trimmed = (rawValue ?? otherTextByGroup[groupKey] ?? '').trim()
    if (trimmed) {
      const parts = trimmed
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
      const unique = new Set(value)
      const next = [...value]
      for (const part of parts.length > 0 ? parts : [trimmed]) {
        if (!unique.has(part)) {
          unique.add(part)
          next.push(part)
          setCustomEntryGroupByValue((prev) => ({ ...prev, [part]: groupKey }))
        }
      }
      if (next.length !== value.length) {
        onChange(next)
      }
    }
    setOtherTextByGroup((prev) => ({ ...prev, [groupKey]: '' }))
  }

  const handleBlur = (groupKey: string, rawValue: string) => {
    if (rawValue.trim()) {
      addCustomEntry(groupKey, rawValue)
    }
  }

  const removeCustomEntry = (entry: string) => {
    onChange(value.filter((v) => v !== entry))
    setCustomEntryGroupByValue((prev) => {
      const next = { ...prev }
      delete next[entry]
      return next
    })
  }

  const grouped = useMemo(() => {
    const map = new Map<string | undefined, typeof options>()
    for (const opt of options) {
      const group = opt.group
      if (!map.has(group)) map.set(group, [])
      map.get(group)!.push(opt)
    }
    return map
  }, [options])
  const hasCategorizedGroups = useMemo(
    () => Array.from(grouped.keys()).some((group) => Boolean(group)),
    [grouped],
  )
  const hasUngroupedOptions = grouped.has(undefined)
  const getGroupKey = (group?: string) => group ?? UNGROUPED_KEY
  const customEntriesByGroup = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const entry of customEntries) {
      const groupKey = customEntryGroupByValue[entry] ?? UNGROUPED_KEY
      const list = map.get(groupKey)
      if (list) list.push(entry)
      else map.set(groupKey, [entry])
    }
    return map
  }, [customEntries, customEntryGroupByValue])

  const renderOption = (opt: (typeof options)[number]) => {
    const checked = value.includes(opt.value)
    return (
      <label
        key={opt.value}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2.5 rounded-sm border px-5 py-3 text-left transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
          checked
            ? 'border-ring/50 bg-primary-xsubtle'
            : 'border-border bg-card hover:border-primary/50 hover:bg-muted/40 dark:hover:border-border-divider-hover'
        )}
      >
        <WizardOptionCheckbox
          checked={checked}
          onCheckedChange={() => toggleOption(opt.value)}
        />
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-medium text-text-secondary">{opt.label}</div>
          {opt.description && (
            <div className="mt-0.5 font-body text-xs leading-[1.45] text-text-subtle">{opt.description}</div>
          )}
        </div>
        {opt.recommended === true && (
          <span className='shrink-0 rounded-sm bg-cobalt-tonal px-2 py-0.5 font-heading text-[10px] font-medium tracking-[0.3px] text-cobalt-strong'>
            Recommended
          </span>
        )}
        {opt.typeILabel && (
          <span
            className={cn(
              'shrink-0 rounded-sm px-2 py-0.5 text-[10px] font-medium',
              opt.typeILabel === 'Suggested'
                ? suggestedTypeILabelClass
                : 'bg-background text-text-subtle'
            )}
          >
            {opt.typeILabel}
          </span>
        )}
      </label>
    )
  }

  const renderOtherInput = (groupKey: string, placeholder: string, className?: string) => (
    <div className={cn('flex items-center gap-2', className)}>
      <input
        type="text"
        className="flex-1 border-b-2 border-border bg-card px-2 py-2 font-body text-[12px] text-text-secondary outline-none transition-colors placeholder:text-text-placeholder focus:border-primary"
        placeholder={placeholder}
        value={otherTextByGroup[groupKey] ?? ''}
        onChange={(e) =>
          setOtherTextByGroup((prev) => ({
            ...prev,
            [groupKey]: e.target.value,
          }))
        }
        onBlur={(e) => handleBlur(groupKey, e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            e.stopPropagation()
            addCustomEntry(groupKey, e.currentTarget.value)
          }
        }}
      />
      {(otherTextByGroup[groupKey] ?? '').trim() && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            addCustomEntry(groupKey)
          }}
          className="shrink-0 rounded-sm border border-border-divider bg-card px-2.5 py-1 font-heading text-[11px] font-medium text-text-body transition-colors hover:border-ring/40 hover:bg-primary-xsubtle"
        >
          Add
        </button>
      )}
    </div>
  )

  const renderCustomEntries = (entries: string[]) => (
    <div className="flex flex-wrap gap-2 pt-2">
      {entries.map((entry) => (
        <span
          key={entry}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 font-body text-[10px] font-normal text-text-body"
        >
          {entry}
          <button
            type="button"
            onClick={() => removeCustomEntry(entry)}
            className="ml-0.5 rounded-full p-0.5 text-icon-muted transition-colors hover:bg-card hover:text-text-body"
            aria-label={`Remove ${entry}`}
          >
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
          </button>
        </span>
      ))}
    </div>
  )

  // With recommended options the shortcut selects only those; otherwise it
  // selects the whole list.
  const recommendedValues = options
    .filter((opt) => opt.recommended === true)
    .map((opt) => opt.value)
  const shortcutValues =
    recommendedValues.length > 0
      ? recommendedValues
      : options.map((opt) => opt.value)
  const allSelected =
    shortcutValues.length > 0 && shortcutValues.every((v) => value.includes(v))
  // Custom "Other" entries are the user's own words — a Clear all that wiped
  // them would delete typing, not just undo checkboxes.
  const toggleSelectAll = () => {
    onChange(
      allSelected
        ? value.filter((v) => !knownValues.has(v))
        : [...new Set([...value, ...shortcutValues])],
    )
  }

  return (
    <div className="space-y-3">
      {allowSelectAll && options.length > 1 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={toggleSelectAll}
            className="font-heading text-xs font-medium text-cobalt-strong transition-opacity hover:opacity-80"
          >
            {allSelected
              ? 'Clear all'
              : recommendedValues.length > 0
                ? 'Select recommended'
                : 'Select all'}
          </button>
        </div>
      )}
      {Array.from(grouped.entries()).map(([group, groupOptions]) => (
        <div key={group ?? '__ungrouped'}>
          {group && (
            <div className="mb-2 mt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground first:mt-0">
              {group}
            </div>
          )}
          <div className="space-y-2">
            {groupOptions.map(renderOption)}
          </div>
          {hasCategorizedGroups && group && (
            <div className="pt-2">
              {renderOtherInput(getGroupKey(group), `Other in ${group}: type and press Enter`)}
              {(customEntriesByGroup.get(getGroupKey(group))?.length ?? 0) > 0 &&
                renderCustomEntries(customEntriesByGroup.get(getGroupKey(group)) ?? [])}
            </div>
          )}
        </div>
      ))}

      {(!hasCategorizedGroups || hasUngroupedOptions) && (
        <div className="pt-1">
          {renderOtherInput(
            getGroupKey(undefined),
            'Other: type and press Enter',
          )}
          {(customEntriesByGroup.get(getGroupKey(undefined))?.length ?? 0) > 0 &&
            renderCustomEntries(customEntriesByGroup.get(getGroupKey(undefined)) ?? [])}
        </div>
      )}
    </div>
  )
}

// ---------- ProductListInput ----------

export interface ProductEntry {
  name: string
  description: string
}

export function ProductListInput({
  value,
  onChange,
  suggestions,
}: {
  value: ProductEntry[]
  onChange: (v: ProductEntry[]) => void
  /**
   * Products read off the public site. Offered, never applied: a homepage
   * markets a flagship, it does not enumerate an audit boundary, so the
   * user adds what belongs and ignores the rest.
   */
  suggestions?: ProductEntry[]
}) {
  // Rows ARE the answer: typing into a row puts that product in scope, so
  // there is no hidden "press Enter to add" step between typing a name and
  // Continue. An empty list shows one blank row to type into; rows without a
  // name are ignored by validation and when the answer is saved.
  const rows = value.length > 0 ? value : [{ name: '', description: '' }]
  const nameRefs = useRef<Array<HTMLInputElement | null>>([])
  const [focusRow, setFocusRow] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (focusRow === null) return
    nameRefs.current[focusRow]?.focus()
    setFocusRow(null)
  }, [focusRow])

  const key = (name: string) => name.trim().toLowerCase()
  const filled = (p: ProductEntry) =>
    p.name.trim().length > 0 || p.description.trim().length > 0
  const pending = (suggestions ?? []).filter(
    (sug) =>
      sug.name.trim().length > 0 &&
      !dismissed.has(key(sug.name)) &&
      // Already added (by hand or from a previous click) — stop offering it.
      !value.some((p) => key(p.name) === key(sug.name))
  )
  const acceptSuggestion = (sug: ProductEntry) => {
    onChange([
      ...value.filter(filled),
      { name: sug.name.trim(), description: sug.description.trim() },
    ])
  }
  const acceptAllSuggestions = () => {
    onChange([
      ...value.filter(filled),
      ...pending.map((sug) => ({
        name: sug.name.trim(),
        description: sug.description.trim(),
      })),
    ])
  }
  const dismissSuggestion = (sug: ProductEntry) => {
    setDismissed((prev) => new Set(prev).add(key(sug.name)))
  }

  const updateProduct = (
    idx: number,
    field: keyof ProductEntry,
    fieldValue: string
  ) => {
    onChange(rows.map((p, i) => (i === idx ? { ...p, [field]: fieldValue } : p)))
  }
  const removeProduct = (idx: number) => {
    onChange(rows.filter((_, i) => i !== idx))
  }
  const lastRowNamed = rows[rows.length - 1].name.trim().length > 0
  const addRow = () => {
    if (!lastRowNamed) {
      setFocusRow(rows.length - 1)
      return
    }
    onChange([...rows, { name: '', description: '' }])
    setFocusRow(rows.length)
  }

  return (
    <div className="space-y-3">
      {rows.map((product, idx) => (
        <div
          key={idx}
          className="flex items-start gap-3 rounded-md border bg-card px-5 py-4"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <input
              ref={(el) => {
                nameRefs.current[idx] = el
              }}
              type="text"
              aria-label={`Product ${idx + 1} name`}
              className="w-full border-b border-border bg-transparent pb-1 font-heading text-sm font-medium text-text-secondary outline-none transition-colors placeholder:text-text-placeholder focus:border-primary"
              value={product.name}
              title={product.name || undefined}
              onChange={(e) => updateProduct(idx, 'name', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && product.name.trim()) {
                  e.preventDefault()
                  addRow()
                }
              }}
              placeholder="Product name, e.g. Acme Cloud"
            />
            <input
              type="text"
              aria-label={`Product ${idx + 1} description`}
              className="w-full border-b border-border bg-transparent pb-1 font-body text-xs text-text-subtle outline-none transition-colors placeholder:text-text-placeholder focus:border-primary"
              value={product.description}
              title={product.description || undefined}
              onChange={(e) => updateProduct(idx, 'description', e.target.value)}
              placeholder="What it does (optional), e.g. B2B SaaS for incident management"
            />
          </div>
          {(rows.length > 1 || filled(product)) && (
            <button
              type="button"
              onClick={() => removeProduct(idx)}
              className="shrink-0 rounded-full p-1 text-icon-muted transition-colors hover:bg-background hover:text-text-body"
              aria-label={`Remove ${product.name || `product ${idx + 1}`}`}
            >
              <svg
                viewBox="0 0 12 12"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
              </svg>
            </button>
          )}
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        disabled={!lastRowNamed}
      >
        <Plus className="h-3.5 w-3.5" />
        Add another product
      </Button>

      {/* Read off the public site — visually distinct from saved rows (dashed,
          muted) so a suggestion is never mistaken for something already in
          scope. Nothing here is in scope until the user says so, so the accept
          action is the loudest thing on the row: these rows are the only way
          out of an empty list, and an accept the user can't find reads to them
          as a dead Continue button. */}
      {pending.length > 0 && (
        <div className="flex items-center justify-between gap-3">
          <span className="font-body text-xs text-text-subtle">
            {pending.length === 1
              ? '1 product found on your site. Add it if it belongs in scope'
              : `${pending.length} products found on your site. Add the ones that belong in scope`}
          </span>
          {pending.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => acceptAllSuggestions()}
            >
              Add all
            </Button>
          )}
        </div>
      )}
      {pending.map((sug) => (
        <div
          key={`suggestion-${sug.name}`}
          className="flex items-center gap-3 rounded-sm border border-dashed border-border bg-surface-subtle px-5 py-4 transition-colors hover:border-ring/40 hover:bg-card"
        >
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-heading text-sm font-medium text-text-secondary">
                {sug.name}
              </span>
              <Badge status="blue">Found on your site</Badge>
            </div>
            {sug.description && (
              <div className="truncate font-body text-xs text-text-subtle">
                {sug.description}
              </div>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => acceptSuggestion(sug)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add to scope
          </Button>
          <button
            type="button"
            onClick={() => dismissSuggestion(sug)}
            className="shrink-0 rounded-full p-1 text-icon-muted transition-colors hover:bg-background hover:text-text-body"
            aria-label={`Dismiss ${sug.name}`}
          >
            <svg
              viewBox="0 0 12 12"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}
